import { Link, NavLink, Outlet } from 'react-router-dom'
import { ArrowRight, Facebook, Github, Instagram, Menu, Twitter, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/cn'
import { brandAssets } from '@/config/brandAssets'
import { CtaLink } from '@/components/marketing/CtaLink'

const navigation = [
  { label: 'Home', href: '/' },
  { label: 'Platform', href: '/#platform' },
  { label: 'Modules', href: '/#modules' },
  { label: 'Security', href: '/#security' },
]

const footerNav = [
  { label: 'Platform', href: '/#platform' },
  { label: 'Modules', href: '/#modules' },
  { label: 'Security', href: '/#security' },
  { label: 'Pricing', href: '/pricing' },
]

const socialLinks = [
  { label: 'Twitter', href: 'https://twitter.com', icon: Twitter },
  { label: 'Facebook', href: 'https://facebook.com', icon: Facebook },
  { label: 'Instagram', href: 'https://instagram.com', icon: Instagram },
  { label: 'GitHub', href: 'https://github.com', icon: Github },
]

/**
 * Shared shell for Kaamco's public marketing pages.
 */
export function PublicSiteLayout(): React.ReactElement {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="public-site min-h-dvh bg-[var(--public-bg)] text-[var(--public-ink)]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-[var(--public-ink)]"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-50 px-4 pt-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 rounded-2xl border border-white/70 bg-white/85 px-4 py-3 shadow-[0_16px_45px_rgba(11,35,64,0.08)] backdrop-blur-xl sm:px-6">
          <Link to="/" className="flex items-center gap-3 self-center leading-none" aria-label="Kaamco home">
            <img src={brandAssets.logo} alt="" className="h-9 w-9 rounded-xl object-contain" aria-hidden />
            <span className="font-display text-base font-semibold tracking-tight">Kaamco</span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
            {navigation.map((item) =>
              item.href.startsWith('/#') ? (
                <a
                  key={item.label}
                  href={item.href}
                  className="rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--public-muted)] transition-colors hover:text-[var(--public-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--public-accent)]"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.href}
                  end
                  className={({ isActive }) =>
                    cn(
                      'rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.12em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--public-accent)]',
                      isActive ? 'text-[var(--public-cta)]' : 'text-[var(--public-muted)] hover:text-[var(--public-ink)]'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden self-center items-center gap-3 lg:flex">
            <a
              href="mailto:hello@kaamco.com?subject=Request%20a%20Kaamco%20demo"
              className="text-sm font-medium text-[var(--public-muted)] transition-colors hover:text-[var(--public-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--public-accent)]"
            >
              Request Demo
            </a>
            <CtaLink to="/login" size="sm">
              Sign In
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </CtaLink>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white/75 text-[var(--public-ink)] transition-colors hover:bg-white lg:hidden"
            aria-expanded={mobileMenuOpen}
            aria-controls="public-mobile-nav"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setMobileMenuOpen((current) => !current)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </button>
        </div>

        {mobileMenuOpen ? (
          <div
            id="public-mobile-nav"
            className="mx-auto mt-2 max-w-7xl rounded-2xl border border-white/60 bg-white px-4 py-4 shadow-[0_24px_48px_rgba(11,35,64,0.08)] lg:hidden"
          >
            <nav aria-label="Mobile primary" className="flex flex-col gap-2">
              {navigation.map((item) =>
                item.href.startsWith('/#') ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--public-ink)] hover:bg-[var(--public-soft)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--public-ink)] hover:bg-[var(--public-soft)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <Link
                to="/pricing"
                className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--public-ink)] hover:bg-[var(--public-soft)]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <a
                href="mailto:hello@kaamco.com?subject=Request%20a%20Kaamco%20demo"
                className="rounded-2xl px-4 py-3 text-sm font-medium text-[var(--public-ink)] hover:bg-[var(--public-soft)]"
              >
                Request Demo
              </a>
              <CtaLink to="/login" className="mt-2 w-full">
                Sign In
              </CtaLink>
            </nav>
          </div>
        ) : null}
      </header>

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--public-border)] bg-[var(--public-panel)]">
        {/* Big CTA */}
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-28">
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight text-[var(--public-ink)] sm:text-5xl">
            Ready to unify your workforce across every company you operate?
          </h2>
          <div className="mt-8 flex justify-center">
            <CtaLink to="/login" className="px-8 text-base">
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </CtaLink>
          </div>
        </div>

        {/* Nav + social row */}
        <div className="border-t border-[var(--public-border)]">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <Link to="/" className="flex items-center gap-3" aria-label="Kaamco home">
              <img src={brandAssets.logo} alt="" className="h-9 w-9 rounded-xl object-contain" aria-hidden />
              <span className="font-display text-lg font-semibold tracking-tight">Kaamco</span>
            </Link>

            <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-8 gap-y-3">
              {footerNav.map((item) =>
                item.href.startsWith('/#') ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm font-medium text-[var(--public-muted)] transition-colors hover:text-[var(--public-ink)]"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-sm font-medium text-[var(--public-muted)] transition-colors hover:text-[var(--public-ink)]"
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--public-border)] text-[var(--public-muted)] transition-colors hover:border-[var(--public-ink)] hover:text-[var(--public-ink)]"
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </a>
                )
              })}
            </div>
          </div>
        </div>

        {/* Legal row */}
        <div className="border-t border-[var(--public-border)]">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-sm text-[var(--public-muted)] sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
            <p>© {new Date().getFullYear()} Kaamco. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="mailto:legal@kaamco.com?subject=Kaamco%20privacy%20request" className="hover:text-[var(--public-ink)]">
                Privacy Policy
              </a>
              <a href="mailto:legal@kaamco.com?subject=Kaamco%20terms%20request" className="hover:text-[var(--public-ink)]">
                Terms &amp; Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
